'use client';

import { useState } from 'react';
import { useClientRendering } from '@/hooks/useClientRendering';
import useSendAnalytics from '@/hooks/useSendAnalytics';
import { ListAnalytics } from '@/types/ListEntry';
import { gtmService } from '@/util/gtm-service';
import { useCategories } from '@/hooks/useCategories';
import { EntryListQuery } from '@/features/EntryList/searchParams';

interface EntryListAnalyticsProps {
  readonly analytics: ListAnalytics;
  readonly query: EntryListQuery;
  readonly ecommerceItems?: Gtag.Item[];
  readonly pageCount?: number;
  readonly totalItemCount?: number;
}

export function EntryListAnalytics({
  analytics,
  query,
  ecommerceItems,
  pageCount,
  totalItemCount,
}: EntryListAnalyticsProps) {
  const [isSent, setIsSent] = useState(false);
  const sendAnalytics = useSendAnalytics();
  const isClient = useClientRendering();
  const { data: categoriesData } = useCategories();

  if (!isSent && isClient && ecommerceItems !== undefined && categoriesData !== undefined) {
    const flatCategories = categoriesData.categories.flatMap((c) => [c, ...Object.values(c.children)]);
    const category = flatCategories.find((c) => c.id === query.filter.category);
    const parentCategory = flatCategories.find((c) => c.id === category?.parentId);

    const custom =
      category !== undefined
        ? {
            parent_category: parentCategory?.name,
            parent_category_id: parentCategory?.id,
            category: category?.name,
            category_id: category?.id,
          }
        : undefined;

    gtmService.recommendedEcommerce.viewItemList(analytics.listId, analytics.listName, ecommerceItems, custom);
    sendAnalytics('entryListPage', {
      category: query.filter.category,
      searchTerm: query.filter.searchTerm,
      sorting: query.sort,
      area: query.filter.region,
      met: query.filter.soldForHighestBidder ?? false,
      itemType: query.filter.type,
      pageNumber: query.page,
      pageCount,
      resultsCount: totalItemCount,
    });
    setIsSent(true);
  }

  return null;
}
